/** Toasts: short confirmations ("Transfer approved"), bottom on phones, top-right on desktop. */
import { AlertTriangle, CheckCircle2, Info, X } from 'lucide-react';
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { cx } from './core';

type Kind = 'success' | 'error' | 'info';
interface Toast {
  id: number;
  kind: Kind;
  text: ReactNode;
  action?: { label: string; onClick: () => void };
}
interface ToastApi {
  show: (text: ReactNode, kind?: Kind, action?: Toast['action']) => void;
  success: (text: ReactNode) => void;
  error: (text: ReactNode) => void;
}

const Ctx = createContext<ToastApi | null>(null);
let seq = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [list, setList] = useState<Toast[]>([]);
  const dismiss = useCallback((id: number) => setList((l) => l.filter((t) => t.id !== id)), []);
  const show = useCallback<ToastApi['show']>(
    (text, kind = 'success', action) => {
      const id = ++seq;
      setList((l) => [...l.slice(-3), { id, kind, text, action }]);
      setTimeout(() => dismiss(id), kind === 'error' ? 8000 : 4000);
    },
    [dismiss],
  );
  const api = useMemo<ToastApi>(() => ({ show, success: (t) => show(t, 'success'), error: (t) => show(t, 'error') }), [show]);
  return (
    <Ctx.Provider value={api}>
      {children}
      <div aria-live="polite" className="pointer-events-none fixed inset-x-0 bottom-0 z-[60] flex flex-col items-center gap-2 p-4 sm:inset-x-auto sm:right-0 sm:top-14 sm:bottom-auto sm:items-end">
        {list.map((t) => (
          <div
            key={t.id}
            role={t.kind === 'error' ? 'alert' : 'status'}
            className={cx(
              'pointer-events-auto flex w-full max-w-sm items-start gap-2.5 rounded-tag border bg-card px-3.5 py-3 text-sm shadow-lift',
              t.kind === 'error' ? 'border-danger/40' : 'border-line',
            )}
          >
            {t.kind === 'success' && <CheckCircle2 aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-brand" />}
            {t.kind === 'error' && <AlertTriangle aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-danger" />}
            {t.kind === 'info' && <Info aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-info" />}
            <div className="min-w-0 flex-1">{t.text}</div>
            {t.action && (
              <button type="button" className="font-semibold text-brand" onClick={() => { t.action!.onClick(); dismiss(t.id); }}>
                {t.action.label}
              </button>
            )}
            <button type="button" aria-label="Dismiss" onClick={() => dismiss(t.id)} className="-m-1 grid h-7 w-7 place-items-center text-muted hover:text-ink">
              <X aria-hidden className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
    </Ctx.Provider>
  );
}

export function useToast(): ToastApi {
  const v = useContext(Ctx);
  if (!v) throw new Error('useToast outside ToastProvider');
  return v;
}
